export const findByValue = <T extends { value: string }>(
  arr: T[],
  v?: string | null
) => arr.find((o) => o.value === v) ?? null

import * as XLSX from 'xlsx'

interface SheetData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

interface ExcelData {
  fileName: string
  [sheetName: string]: string | SheetData[]
}

export const downloadExcel = (excelData: ExcelData): void => {
  try {
    const { fileName, ...sheets } = excelData
    const workbook = XLSX.utils.book_new()
    Object.entries(sheets).forEach(([sheetName, data]) => {
      if (Array.isArray(data) && data.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(data)
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
      } else {
        console.warn(`Sheet "${sheetName}" has no data or invalid format`)
        const emptySheet = XLSX.utils.json_to_sheet([])
        XLSX.utils.book_append_sheet(workbook, emptySheet, sheetName)
      }
    })

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    })

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${fileName}.xlsx`

    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading Excel file:', error)
    throw new Error('Failed to download Excel file')
  }
}

interface ExcelOptions {
  dateNF?: string // Date number format
  cellDates?: boolean // Use cell dates
  sheetStubs?: boolean // Include empty cells
}

export const downloadExcelAdvanced = (
  excelData: ExcelData,
  options: ExcelOptions = {}
): void => {
  try {
    const { fileName, ...sheets } = excelData

    const workbook = XLSX.utils.book_new()

    Object.entries(sheets).forEach(([sheetName, data]) => {
      if (Array.isArray(data) && data.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(data)

        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1')
        const colWidths: Array<{ wch: number }> = []

        for (let C = range.s.c; C <= range.e.c; ++C) {
          let maxWidth = 10 // minimum width

          for (let R = range.s.r; R <= range.e.r; ++R) {
            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
            const cell = worksheet[cellAddress]

            if (cell && cell.v) {
              const cellLength = cell.v.toString().length
              maxWidth = Math.max(maxWidth, cellLength)
            }
          }

          colWidths.push({ wch: Math.min(maxWidth + 2, 50) }) // max width 50
        }

        worksheet['!cols'] = colWidths
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
      }
    })

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
      cellDates: options.cellDates || true,
      ...options
    })

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${fileName}.xlsx`

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading Excel file:', error)
    throw new Error('Failed to download Excel file')
  }
}
